import { useTranslation } from '@rhino-project/core/i18n';
import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  AlertDialog,
  Button,
  Card,
  Fieldset,
  FieldsetActions,
  FieldsetLegend,
  Form,
  Tag,
  TagGroup,
  Typography
} from '@heroui/react';
import {
  FormErrors,
  ModelCreateSimple,
  ModelEditSimple,
  ModelFieldGroup
} from '@rhino-project/ui-heroui';
import { Icon } from '@iconify/react';
import {
  useModelCreateContext,
  useModelDelete,
  useModelEditContext,
  useModelIndex
} from '@rhino-project/core/hooks';
import { useFormContext } from 'react-hook-form';
import { getYouTubeThumbnail } from '../../../../../../utils/youtube';
import { formatVideoLabel } from '../../../../../../utils/videoConstants';
import type { RhinoRead } from '@rhino-project/core';

type Video = RhinoRead<'video'>;

const FIELD_PATHS = ['title', 'url', 'category', 'system', 'description'];

type FormMode =
  { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; id: number };

const formFields = (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ModelFieldGroup path="title" variant="secondary" />
      <ModelFieldGroup path="url" variant="secondary" />
    </div>
    <ModelFieldGroup path="system" variant="secondary" />
    <ModelFieldGroup path="description" variant="secondary" />
  </>
);

const SystemCreateForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelCreateContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Form>
        <Fieldset>
          <FieldsetLegend className="text-lg font-semibold">
            {t('frontend.messages.new_team_video')}
          </FieldsetLegend>
          <FormErrors />
          {formFields}
          <FieldsetActions>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={isPending}
              data-testid="system-video-save"
            >
              {t('frontend.messages.add')}
            </Button>
            <Button variant="ghost" onPress={onDone}>
              {t('frontend.messages.cancel')}
            </Button>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </Card>
  );
};

const SystemEditForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelEditContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Form>
        <Fieldset>
          <FieldsetLegend className="text-lg font-semibold">
            {t('frontend.messages.edit_team_video')}
          </FieldsetLegend>
          <FormErrors />
          {formFields}
          <FieldsetActions>
            <Button
              variant="primary"
              onPress={onSave}
              isDisabled={isPending}
              data-testid="system-video-save"
            >
              {t('frontend.messages.save')}
            </Button>
            <Button variant="ghost" onPress={onDone}>
              {t('frontend.messages.cancel')}
            </Button>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </Card>
  );
};

type CardActions = {
  onEdit: (video: Video) => void;
  onDelete: (video: Video) => void;
};

const SystemVideoCard = ({
  video,
  onEdit,
  onDelete
}: { video: Video } & CardActions) => {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-center gap-4">
        <img
          src={getYouTubeThumbnail(video.url)}
          alt={video.title}
          className="w-32 aspect-video object-cover rounded shrink-0"
        />
        <div className="flex-1 min-w-0">
          <Card.Title>{video.title}</Card.Title>
          {video.description && (
            <p className="text-sm text-muted">{video.description}</p>
          )}
          {video.system && (
            <TagGroup
              className="mt-2"
              aria-label={t('frontend.messages.system')}
            >
              <TagGroup.List>
                <Tag id={video.system as string}>
                  {formatVideoLabel(video.system as string)}
                </Tag>
              </TagGroup.List>
            </TagGroup>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('frontend.messages.edit_video')}
            onPress={() => onEdit(video)}
          >
            <Icon icon="bi:pencil" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('frontend.messages.delete_video')}
            onPress={() => onDelete(video)}
            className="text-danger"
          >
            <Icon icon="bi:trash" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const AdminSystemVideos = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false }) as {
    team_season_id: string;
  };
  const teamSeasonId = Number(team_season_id);

  const { results } = useModelIndex('video', {
    filter: {
      team_season_id: teamSeasonId,
      category: 'system'
    },
    order: ['title'],
    limit: 200
  });
  const videos = results ?? [];

  const { mutate: deleteVideo } = useModelDelete('video');

  const [mode, setMode] = useState<FormMode>({ kind: 'none' });
  const [deleting, setDeleting] = useState<Video | null>(null);

  const closeForm = () => setMode({ kind: 'none' });
  const handleEdit = (video: Video) => setMode({ kind: 'edit', id: video.id });

  const handleConfirmDelete = () => {
    if (!deleting) return;
    deleteVideo({ id: deleting.id } as never, {
      onSuccess: () => setDeleting(null)
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Typography.Heading level={1}>
          {t('frontend.messages.team_videos')}
        </Typography.Heading>
        <Button variant="primary" onPress={() => setMode({ kind: 'create' })}>
          <Icon icon="bi:plus" className="mr-1" />
          {t('frontend.messages.add_team_video')}
        </Button>
      </div>

      {mode.kind === 'create' && (
        <ModelCreateSimple
          model="video"
          parentId={teamSeasonId}
          paths={FIELD_PATHS as never}
          extraDefaultValues={{ category: 'system' }}
          fallback={false}
        >
          <SystemCreateForm onDone={closeForm} />
        </ModelCreateSimple>
      )}

      {mode.kind === 'edit' && (
        <ModelEditSimple
          model="video"
          modelId={mode.id}
          paths={FIELD_PATHS as never}
          fallback={false}
        >
          <SystemEditForm onDone={closeForm} />
        </ModelEditSimple>
      )}

      <div className="flex flex-col gap-3">
        {videos.length === 0 ? (
          <p className="text-muted">
            {t('frontend.messages.no_system_videos_yet')}
          </p>
        ) : (
          videos.map((video) => (
            <SystemVideoCard
              key={video.id}
              video={video}
              onEdit={handleEdit}
              onDelete={setDeleting}
            />
          ))
        )}
      </div>

      <AlertDialog
        isOpen={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>
                  {t('frontend.messages.delete_video_2')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {t('frontend.messages.delete_confirmation', {
                  name: deleting?.title as string
                })}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={() => setDeleting(null)}>
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onPress={handleConfirmDelete}
                  data-testid="system-video-delete-confirm"
                >
                  {t('frontend.messages.delete')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
};

export const Route = createFileRoute(
  '/_authenticated/$owner/active/$team_season_id/videos/systems'
)({
  component: AdminSystemVideos
});
